<script setup lang="ts">
  defineOptions({
    name: 'AuthPageLayoutWeb',
  })

  import { computed, ref, watch, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { Mail03Icon } from '@gohighlevel/ghl-icons/24/outline'

  import AuthBrandingHeader from '@common/auth-layer/components/AuthBrandingHeader.vue'

  import AuthHandler from '@common/auth-layer/components/AuthHandler.vue'

  import { Screens, type ScreenChangeData } from '@common/auth-layer/types'

  import { maskEmailForDisplay } from '@common/auth-layer/utils/maskEmailForDisplay'

  import { useLocation } from '@common/location-layer/composables/useLocation'

  import { sanitizeSupportMailtoAddress } from '@common/shared-ui-layer/utils/global-error.utils'

  import AuthScreenHeader from '@clientportal/auth-layer/components/molecules/AuthScreenHeader.vue'

  import { screenChangeStaysInPlace } from '@clientportal/auth-layer/composables/useAuthPage'

  import { resolvePortalLogo } from '@clientportal/auth-layer/utils/branding-assets'

  const props = defineProps({
    initialPage: {
      type: String as PropType<string>,
      default: Screens.LOGIN,
    },
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: undefined,
    },
    isSsoDisabled: {
      type: Boolean,
      default: undefined,
    },
    /**
     * Clientportal: defer OTP/Google session until after in-place Set Password.
     * Default true for `/login`. Set false on `/auth` credential processor so Set Password completion stays in the page callback (single saveAuthAfterLogin).
     */
    deferSetPasswordSession: {
      type: Boolean,
      default: true,
    },
    /**
     * Magic-link OTP handoff only — mask the VERIFY_SECURE_CODE header email.
     * Host-owned (`/auth`); do not seed from history.state (Vue Router merges state).
     */
    maskEmail: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits<{
    (e: 'loginCallback', data: unknown): void
    (e: 'changeScreen', data: ScreenChangeData): void
  }>()

  const handleLoginCallback = (event: unknown) => {
    emit('loginCallback', event)
  }

  const { t } = useI18n()

  const { store: locationStore } = useLocation()

  // Track current screen and email so header and AuthHandler stay in sync (matches LoginModal behaviour)
  const currentScreen = ref(props.initialPage)

  const historyState = globalThis?.history?.state as Record<string, unknown> | null | undefined
  const historyStateEmail = historyState?.email
  const currentUserEmail = ref(typeof historyStateEmail === 'string' ? historyStateEmail : '')
  // Host prop only — dies with the `/auth` handoff layout; Change email is hidden while masked.
  const shouldMaskDisplayEmail = computed(() => props.maskEmail === true)

  const headerEmail = computed(() => (shouldMaskDisplayEmail.value ? maskEmailForDisplay(currentUserEmail.value) : currentUserEmail.value))

  // Branding data from location store
  const portalLogo = computed(() => resolvePortalLogo(locationStore.branding))

  const portalName = computed(() => locationStore.branding.portalBannerText || t('auth.branding.defaultPortalName'))

  const portalDescription = computed(() => locationStore.branding?.portalDescription || null)

  // Sanitize to a bare address so branding values with mailto params (e.g. "?bcc=")
  // cannot pre-populate extra recipients when the Support link is clicked.
  const supportEmail = computed(() => sanitizeSupportMailtoAddress(locationStore.branding?.supportEmail))

  const copyrightText = computed(() => locationStore.branding?.copyRight || t('auth.copyright', { year: new Date().getFullYear() }))

  const hasFooterSupport = computed(() => Boolean(supportEmail.value))

  const hasFooterCopyright = computed(() => Boolean(copyrightText.value))

  const footerJustifyClass = computed(() => (hasFooterSupport.value && hasFooterCopyright.value ? 'justify-between' : 'justify-center'))

  const resolvedIsSsoDisabled = computed(() => {
    if (props.isSsoDisabled !== undefined) return props.isSsoDisabled
    return locationStore.locationData?.isSSODisabled === true
  })

  // Full-page auth is route-driven: a routed screen change is handled by the
  // route remount, so AuthHandler should not swap in place first. In-place
  // screens (verify code, set-password, etc.) still swap instantly.
  const deferRoutedScreenChange = (fromScreen: string, toScreen: string) => !screenChangeStaysInPlace(fromScreen, toScreen)

  const handleChangeScreen = (data: ScreenChangeData) => {
    // Only swap the screen in-place for changes that do NOT navigate (SET_PASSWORD
    // gate, signup-internal verify/back, forgot→proceed, same-route). Routed
    // changes are driven by the route remount (initialPage), so we avoid an
    // instant local swap that would pre-empt and double up the page transition.
    if (data.page && screenChangeStaysInPlace(currentScreen.value, data.page)) {
      currentScreen.value = data.page
      currentUserEmail.value = data.email || ''
    }
    emit('changeScreen', data)
  }

  // Per-screen page header (title + description). One config-driven block keeps
  // every screen's header treatment consistent.
  const makeHeader = (config: { title: string; description?: string }) => ({
    title: config.title,
    description: config.description ?? '',
  })

  const screenHeader = computed(() => {
    switch (currentScreen.value) {
      case Screens.LOGIN:
        return makeHeader({ title: t('auth.titles.login'), description: t('auth.descriptions.login') })
      case Screens.SIGN_UP:
        return makeHeader({ title: t('auth.titles.signUp'), description: t('auth.descriptions.signUp') })
      case Screens.LOGIN_WITH_SECURE_CODE:
        return makeHeader({
          title: t('auth.titles.loginWithCode'),
          description: t('auth.loginModal.enterEmailToLogin'),
        })
      case Screens.FORGOT_PASSWORD:
        return makeHeader({
          title: t('auth.titles.forgotPassword'),
          description: t('auth.descriptions.forgotPassword'),
        })
      case Screens.VERIFY_SECURE_CODE:
        return makeHeader({
          title: t('auth.loginModal.verifySecureCode'),
          description: t('auth.loginModal.sentTo', { email: headerEmail.value }),
        })
      default:
        return null
    }
  })

  const embedsBrandingInChildFlow = computed(
    () => currentScreen.value === Screens.EXPIRED_PASSWORD || currentScreen.value === Screens.PROFILE_CONFIRMATION
  )

  watch(
    () => props.initialPage,
    newPage => {
      if (newPage) currentScreen.value = newPage
    }
  )
</script>

<template>
  <div class="flex w-full flex-col">
    <AuthBrandingHeader
      v-if="!embedsBrandingInChildFlow"
      :show-branding="true"
      :portal-logo="portalLogo"
      :portal-name="portalName"
      :description="portalDescription"
    />

    <div class="flex flex-1 flex-col justify-center">
      <AuthScreenHeader v-if="screenHeader" :title="screenHeader.title" :description="screenHeader.description" />

      <AuthHandler
        :page="currentScreen"
        :referrer-user="referrerUser"
        :show-branding="true"
        :portal-logo="portalLogo"
        :portal-name="portalName"
        :is-sso-disabled="resolvedIsSsoDisabled"
        :defer-set-password-session="deferSetPasswordSession"
        :should-defer-screen-change="deferRoutedScreenChange"
        :show-change-email="!shouldMaskDisplayEmail"
        @login-callback="handleLoginCallback"
        @change-screen="handleChangeScreen"
      />
    </div>

    <!-- Support + copyright footer: pinned to the bottom of the form panel. -->
    <div
      id="cp-auth-footer"
      class="shrink-0 flex flex-wrap items-center gap-2 pt-8 mt-8 text-sm text-secondary"
      :class="footerJustifyClass"
    >
      <a
        v-if="hasFooterSupport"
        id="cp-auth-support-link"
        :href="`mailto:${supportEmail}`"
        class="flex items-center text-secondary no-underline hover:underline"
      >
        <Mail03Icon class="h-4 w-4" />
        <span class="ml-1">{{ supportEmail }}</span>
      </a>
      <span v-if="hasFooterCopyright">{{ copyrightText }}</span>
    </div>
  </div>
</template>
